import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import React from 'react';
import { useAppSelector } from '../../app/hooks';
import OrderReview from '../../features/order-review/OrderReview';
import OrderTotalCard from './OrderTotalCard';
import PosCartContainer from '../../features/order-review/PosCartContainer';
import { OrderActions } from '../OrderReview/OrderActions';
import { selectCheckEventItems } from '../../selectors/order';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectIsCarPresent } from '../../selectors/message';
import { selectFeatureFlagPosCart } from '../../redux/features/config/config.selector';
import Colors from '../../utils/color';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  card: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    border: `${spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: spacing(1),
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.75),
    padding: spacing(1.5),
    borderBottom: `${spacing(0.125)} solid ${Colors.alto}`,
  },
  headerIcon: {
    color: Colors.readBack,
    fontSize: '1.1rem',
  },
  title: {
    flex: 1,
    color: Colors.readBack,
    fontWeight: 700,
    fontSize: '0.8rem',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  count: {
    color: Colors.silverChalice,
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  itemsScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: spacing(1),
  },
}));

// Mirrors RightBar's conversion for the optional POS cart debug panel.
const convertCheckEventToOrderItems = (checkEventItems: any[]) => {
  const convertOptions = (options: any[]): any[] =>
    options?.map((option: any) => ({
      name: option.name,
      pos_id: option.pos_id?.toString() || option.id?.toString() || '',
      quantity: option.quantity || 1,
      price: option.price || '0.00',
      options: convertOptions(option.options || []),
    })) || [];

  return checkEventItems.map((item) => ({
    name: item.name,
    pos_id: item.pos_id?.toString() || item.id?.toString() || '',
    quantity: item.quantity || 1,
    price: item.price || '0.00',
    options: convertOptions(item.options || []),
  }));
};

/**
 * Agent Cart card (right pane). Reuses the existing order-review pieces:
 *   - OrderReview         -> cart line items + modifiers
 *   - OrderTotalCard      -> subtotal / tax / total
 *   - OrderActions        -> Cancel Order / Finish Order
 * under an "AGENT CART" heading with item count + divider.
 */
const AgentCartCard = () => {
  const classes = useStyles();
  const itemQuantities = useAppSelector(selectCartItemsQuantity);
  const checkEventItems = useAppSelector(selectCheckEventItems);
  const isPosCartEnabled = useAppSelector(selectFeatureFlagPosCart);
  const isCarPresent = useAppSelector(selectIsCarPresent);

  // selectCartItemsQuantity is a per-item map; total = sum of the quantities.
  const itemCount = React.useMemo(
    () =>
      Object.values(itemQuantities ?? {}).reduce(
        (sum, qty) => sum + (qty || 0),
        0
      ),
    [itemQuantities]
  );

  const orderItems = React.useMemo(
    () => convertCheckEventToOrderItems(checkEventItems),
    [checkEventItems]
  );

  return (
    <div id="agent-cart" className={classes.card}>
      <div className={classes.header}>
        <ShoppingCartOutlinedIcon className={classes.headerIcon} />
        <span className={classes.title}>Agent Cart</span>
        {isCarPresent && (
          <span className={classes.count}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </span>
        )}
      </div>
      <div className={classes.itemsScroll}>
        <OrderReview />
      </div>
      {isPosCartEnabled && <PosCartContainer items={orderItems} />}
      <OrderTotalCard />
      <OrderActions />
    </div>
  );
};

export default AgentCartCard;
